import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import Lottie from "lottie-react";

import {
  getDocusignTransactions,
  getDocusignPDF,
  getFormsySubmissionForClient,
  getFormComments,
  saveFormComment,
  deactivateFormComment,
  getFormContainersAndVersions,
  getFormsySubmissionsBySubmissionIds,
} from "../services/soar";
import {
  sendVoidRequest,
  resendEnvelope,
  getAuditHistory,
} from "../services/docusignService";
import { lookupFormInfov2 } from "../forms/forms";
import { groupBy, map } from "lodash";
import SearchBox from "../common/SearchBox";
import ExpandDiv from "../common/ExpandDiv";
import Input from "../common/Input";
import * as documentsLoading from "../../animations/file-folder.json";
import { toast } from "react-toastify";
import DocusignAuditHistory from "./DocusignAuditHistory";
import { Modal } from "react-bootstrap";

function FormHistory({ clientProgramId, clientName, contactId }) {
  const location = useLocation();
  const [envelopeHistory, setEnvelopeHistory] = useState({});
  const [documents, setDocuments] = useState([]);
  const [formHistoryLoaded, setFormHistoryLoaded] = useState(false);
  const [historyView, setHistoryView] = useState("Summary");
  const [searchQuery, setSearchQuery] = useState("");
  const [beginDate, setBeginDate] = useState();
  const [endDate, setEndDate] = useState();
  const [formNotes, setFormNotes] = useState([]);
  const [selectedNotes, setSelectedNotes] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [note, setNote] = useState("");
  const [noteText, setNoteText] = useState("");
  const [auditHistory, setAuditHistory] = useState({});
  const [auditIsOpen, setAuditIsOpen] = useState(false);
  const [extraFieldsLoaded, setExtraFieldsLoaded] = useState(false);
  const isMountedRef = useRef(true);
  const dsorg =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "https://app.docusign.com"
      : "https://apps-d.docusign.com";
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  const getHistory = useCallback(async () => {
    setFormHistoryLoaded(false);
    setEnvelopeHistory({});
    setDocuments([]);
    setExtraFieldsLoaded(false);

    const response = await Promise.all([
      getFormsySubmissionForClient(contactId),
      getDocusignTransactions(clientProgramId),
      getFormComments(contactId),
    ]);

    if (!isMountedRef.current) {
      return;
    }

    console.log("getHistoryPromiseResponse: ", response);

    const formsyReponse = response[0];
    const docusignResponse = response[1];
    const formNotes = response[2];
    console.log("formNotes", formNotes);
    setFormNotes(formNotes.data.filter((c) => c.Active === true));

    let formsyHistory = JSON.parse(formsyReponse).Submissions.Submission;
    formsyHistory =
      formsyHistory !== undefined
        ? formsyHistory.filter(
            (submission) =>
              submission.Active === "1" &&
              (submission.ClientProgramId === "" ||
                submission.ClientProgramId === clientProgramId.slice(1, 37))
          )
        : [];

    const getEnvelopeHistory = docusignResponse;

    console.log(
      "Autocompleted",
      docusignResponse.filter(
        (transaction) => transaction.status === "safyautocompleted"
      )
    );
    console.log("Docusign History JSON", getEnvelopeHistory);

    let grouped = groupBy(getEnvelopeHistory, "envelopeId");
    setEnvelopeHistory(grouped);

    const docs = [];

    if (formsyHistory.length > 0) {
      const formInfo = await getFormContainersAndVersions();

      formsyHistory.forEach((formsy) => {
        const formDetails = lookupFormInfov2(
          formInfo,
          formsy.FormName,
          formsy.FormVersionId
        );

        if (formDetails?.formName && formDetails.formPath) {
          docs.push({
            date: `${formsy.CreatedOn}Z`,
            name: formDetails.formName,
            documentId: formsy.SubmissionId,
            status: formsy.FinalSigned === "1" ? "Fully Signed" : "Unsigned",
            type: "formsy",
            formPath: formDetails.formPath,
            formVersionNumber: formsy.VersionIntId,
            extraFields: formDetails.extraFields,
            ...formsy,
          });
        }
      });
    }

    map(grouped, (envelope) => {
      //sort newest first
      let sortedEnvelope = envelope.sort((a, b) => {
        const aDate = new Date(a.logCreated);
        const bDate = new Date(b.logCreated);
        if (aDate < bDate) return 1;
        if (aDate > bDate) return -1;
        return 0;
      });

      const allEnvelopeDocs =
        sortedEnvelope[0].status !== "safyautocompleted"
          ? JSON.parse(sortedEnvelope[0].jsonData).envelopeDocuments.filter(
              (doc) => doc.name !== "Summary"
            )
          : JSON.parse(sortedEnvelope[1].jsonData).envelopeDocuments.filter(
              (doc) => doc.name !== "Summary"
            );

      allEnvelopeDocs.forEach((d) => {
        docs.push({
          date: `${sortedEnvelope[0].sentDateTime}Z`,
          name: d.name,
          documentId: d.documentIdGuid,
          envelopeId: sortedEnvelope[0].envelopeId,
          status:
            sortedEnvelope[0].status === "completed"
              ? "Fully Signed"
              : sortedEnvelope[0].status,
          type: "docusign",
          envelope: {
            ...sortedEnvelope[0],
          },
          autocompleted:
            sortedEnvelope.filter(
              (transaction) => transaction.status === "safyautocompleted"
            ).length > 0,
          ...d,
        });
      });

      return <></>;
    });

    setDocuments(docs);
    setFormHistoryLoaded(true);
  }, [contactId, clientProgramId]);

  useEffect(() => {
    if (extraFieldsLoaded || documents.length === 0) {
      return;
    }

    const loadExtraFields = async () => {
      const submissionIds = documents
        .filter((d) => d.extraFields)
        .map((d) => d.SubmissionId);

      if (submissionIds.length > 0) {
        const response = await getFormsySubmissionsBySubmissionIds(
          submissionIds
        );
        const result = JSON.parse(response);
        result.FormSubmissions.Submission.forEach((r) => {
          const doc = documents.find((d) => d.SubmissionId === r.SubmissionId);
          doc.extraFields.forEach((field) => {
            field.value = r.FormResults.results[field.name]["@value"];
          });
        });
      }
      setExtraFieldsLoaded(true);
    };

    loadExtraFields();
  }, [documents, extraFieldsLoaded]);

  useEffect(() => {
    getHistory();
  }, [getHistory]);

  const updateFormNotes = () => {
    getFormComments(contactId).then((response) => {
      console.log("Form notes", response);
      setFormNotes(response.data.filter((c) => c.Active === true));
    });
  };

  const handleSearch = ({ currentTarget: input }) => {
    setSearchQuery(input.value);
    filterDocuments(input.value);
  };

  const handleBeginDate = ({ currentTarget: input }) => {
    setBeginDate(input.value);
    filterDocuments();
  };

  const handleEndDate = ({ currentTarget: input }) => {
    setEndDate(input.value);
    filterDocuments();
  };

  const filterDocuments = () => {
    let filtered = documents;
    if (searchQuery) {
      filtered = filtered.filter((doc) =>
        doc.name.toLowerCase().includes(searchQuery.toLocaleLowerCase())
      );
    }

    if (historyView === "InProcess") {
      filtered = filtered.filter(
        (doc) =>
          doc.status.toLowerCase() === "unsigned" ||
          doc.status.toLowerCase() === "sent" ||
          doc.status.toLowerCase() === "delivered"
      );
    }

    if (historyView === "Completed") {
      filtered = filtered.filter(
        (doc) => doc.status.toLowerCase() === "fully signed"
      );
    }

    if (beginDate && beginDate !== "") {
      filtered = filtered.filter(
        (item) => new Date(item.date) >= new Date(beginDate)
      );
    }

    if (endDate && endDate !== "") {
      let end = new Date(endDate);
      end = end.setDate(end.getDate() + 1);
      filtered = filtered.filter((item) => new Date(item.date) <= end);
    }

    filtered.sort((a, b) => {
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);
      if (aDate < bDate) return 1;
      if (aDate > bDate) return -1;
      return 0;
    });

    return filtered;
  };

  const summaryDocuments = () => {
    let summary = filterDocuments().sort((a, b) => {
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);
      if (aDate < bDate) return 1;
      if (aDate > bDate) return -1;
      return 0;
    });

    const grouped = groupBy(summary, "name");

    console.log("Grouped Summary", grouped);
    return grouped;
  };

  const base64toBlob = (data) => {
    const bytes = atob(data);
    let length = bytes.length;
    let out = new Uint8Array(length);

    while (length--) {
      out[length] = bytes.charCodeAt(length);
    }

    return new Blob([out], { type: "application/pdf" });
  };

  const getEnvelopeDocuments = (envelope, isAutocompleted) => {
    if (envelope) {
      let sortedEnvelope = envelope.sort((a, b) => {
        const aDate = new Date(a.logCreated);
        const bDate = new Date(b.logCreated);
        if (aDate < bDate) return 1;
        if (aDate > bDate) return -1;
        return 0;
      });

      const lastTransaction =
        sortedEnvelope[0].status !== "safyautocompleted"
          ? sortedEnvelope[0]
          : sortedEnvelope[1];
      const envelopeJSON = JSON.parse(lastTransaction.jsonData);

      const initialTransaction = JSON.parse(
        sortedEnvelope[sortedEnvelope.length - 1].jsonData
      ).recipients;

      const signers =
        envelopeJSON.recipients.signers &&
        envelopeJSON.recipients.signers.length > 0 ? (
          <>
            <div>Signers</div>
            {envelopeJSON.recipients.signers.map((signer, index) => (
              <div
                key={`${signer.name}${index}`}
                className={
                  signer.completedCount && signer.completedCount > 0
                    ? "ms-4 fw-normal"
                    : "ms-4 text-danger"
                }
              >
                {`${signer.name}: ${
                  signer.completedCount && signer.completedCount > 0
                    ? `Signed ${new Date(
                        signer.signedDateTime
                      ).toLocaleString()}`
                    : "Not Signed"
                }`}
                {signer.autoRespondedReason && (
                  <div className="border rounded bg-danger text-white mb-2 ps-2">
                    {signer.autoRespondedReason}
                  </div>
                )}
              </div>
            ))}
          </>
        ) : null;

      const removedSigners = initialTransaction.signers
        .filter(
          (signer) =>
            !envelopeJSON.recipients.signers
              .map((s) => s.recipientId)
              .includes(signer.recipientId)
        )
        .map((signer, i) => (
          <div
            key={`${signer.recipientId}-${i}`}
            className="fw-regular text-danger ms-4"
          >{`Removed: ${signer.name}`}</div>
        ));

      const inPersonSigners =
        envelopeJSON.recipients.inPersonSigners &&
        envelopeJSON.recipients.inPersonSigners.length > 0 ? (
          <>
            <div>In Person Signers</div>
            {envelopeJSON.recipients.inPersonSigners.map((signer, index) => (
              <div
                key={`${signer.name}${index}`}
                className={
                  signer.completedCount && signer.completedCount > 0
                    ? "ms-4 fw-normal"
                    : "ms-4 text-danger"
                }
              >
                {`${signer.signerName}(host: ${signer.hostName}): ${
                  signer.completedCount && signer.completedCount > 0
                    ? `Signed ${new Date(
                        signer.signedDateTime
                      ).toLocaleString()}`
                    : "Not Signed"
                }`}
              </div>
            ))}
          </>
        ) : null;

      const removedInPersonSigners = initialTransaction.inPersonSigners
        .filter(
          (signer) =>
            !envelopeJSON.recipients.inPersonSigners
              .map((s) => s.recipientId)
              .includes(signer.recipientId)
        )
        .map((signer, i) => (
          <div
            key={`${signer.recipientId} -${i}`}
            className="fw-regular text-danger ms-4"
          >{`Removed: ${signer.signerName}`}</div>
        ));

      // if (
      //   envelopeJSON.recipients.inPersonSigners &&
      //   envelopeJSON.recipients.inPersonSigners.length > 0
      // ) {
      //   signers += "In Person Signers<br/>";
      //   signers += envelopeJSON.recipients.inPersonSigners.map(
      //     (signer) =>
      //       `${signer.signerName}(host: ${signer.hostName}): ${
      //         signer.completedCount && signer.completedCount > 0
      //           ? "Signed"
      //           : "Not Signed"
      //       } <br/>`
      //   );
      // }

      const envelopeDocuments =
        lastTransaction.status === "completed"
          ? envelopeJSON.envelopeDocuments
          : envelopeJSON.envelopeDocuments.filter(
              (doc) => doc.name !== "Summary"
            );

      const completed = envelope.some((transaction) => {
        if (
          transaction.status === "completed" ||
          transaction.status === "safyautocompleted"
        ) {
          return true;
        }

        return false;
      });

      const docs = envelopeDocuments.map((doc) => (
        <div
          key={doc.documentIdGuid}
          className={
            completed
              ? "rounded border border-dark p-2 m-2 bg-success text-dark"
              : "rounded border border-dark p-2 m-2 bg-warning text-dark"
          }
        >
          <div className="row">
            <div className="col-10 fw-bold">{doc.name}</div>
            {envelopeJSON.envelopeDocuments &&
            envelopeJSON.envelopeDocuments[0].PDFBytes ? (
              <i
                className="fas fa-file-pdf fa-2x text-white"
                onClick={() => {
                  window.open(
                    URL.createObjectURL(
                      base64toBlob(envelopeJSON.envelopeDocuments[0].PDFBytes)
                    )
                  );
                }}
              ></i>
            ) : (
              <></>
            )}
          </div>
        </div>
      ));

      const status = (
        <div className="row">
          <div className="col-6">{`Created: ${new Date(
            envelopeJSON.sentDateTime
          ).toLocaleString()}`}</div>
          <div className="col-6">
            {completed
              ? `Completed: ${new Date(
                  envelopeJSON.completedDateTime
                ).toLocaleString()}`
              : `Status: ${envelopeJSON.status}`}
          </div>
        </div>
      );

      return (
        <div
          className="rounded mx-2 my-4 p-2 border-dark safy-border-2 bg-light fw-bold shadow"
          key={envelope.envelopeId}
          title={lastTransaction.envelopeId}
        >
          {/* <div className="mb-2">{status}</div>
            <div className="row">
              <div className="col-6">{signers}</div>
              <div className="col-6">{inPersonSigners}</div>
            </div>
            <div className="d-flex">{docs}</div> */}
          <div className="row">
            <div className="col-5">{docs}</div>
            <div className="col-7">
              {status}
              {signers}
              {removedSigners}
              {inPersonSigners}
              {removedInPersonSigners}
            </div>
          </div>
        </div>
      );
    }
  };

  const voidEnvelope = async (envelope) => {
    const reason = getVoidReason();
    if (reason !== undefined && reason !== null) {
      // let sortedEnvelope = envelopeHistory[envelope].sort((a, b) => {
      //   const aDate = new Date(a.logCreated);
      //   const bDate = new Date(b.logCreated);
      //   if (aDate < bDate) return 1;
      //   if (aDate > bDate) return -1;
      //   return 0;
      // });

      // const lastTransaction = sortedEnvelope[0];
      // let envJSON = JSON.parse(lastTransaction.jsonData);
      // envJSON.status = "voided";
      // envJSON.voidReason = reason;

      sendVoidRequest(envelope, reason);
      getHistory();
    }
  };

  const openInDocusign = (envelope) => {
    window.open(`${dsorg}/send/documents/details/${envelope.toUpperCase()}`);
  };

  const openDocusignHistory = (envelope) => {
    setAuditHistory({});
    getAuditHistory(envelope).then((resp) => {
      if (!resp.errorCode) {
        setAuditHistory(resp);
        setAuditIsOpen(true);
      } else {
        toast.error(`Error accessing Docusign History: ${resp.errorCode}`);
      }
    });
  };

  const openDocusignCertificate = (envelope) => {
    getDocusignPDF(envelope).then((response) => {
      if (response) {
        const pdfTransaction = JSON.parse(response);
        console.log("pdfTransaction", pdfTransaction);
        if (
          pdfTransaction.Docusign &&
          pdfTransaction.Docusign.Documents &&
          pdfTransaction.Docusign.Documents[0] &&
          pdfTransaction.Docusign.Documents[0].jsonData
        ) {
          const pdfJSON = JSON.parse(
            pdfTransaction.Docusign.Documents[0].jsonData
          );
          if (
            pdfJSON &&
            pdfJSON.envelopeDocuments &&
            pdfJSON.envelopeDocuments[pdfJSON.envelopeDocuments.length - 1]
              .PDFBytes
          ) {
            const pdfBytes =
              pdfJSON.envelopeDocuments[pdfJSON.envelopeDocuments.length - 1]
                .PDFBytes;
            console.log(pdfBytes);
            window.open(URL.createObjectURL(base64toBlob(pdfBytes)));
          } else alert("No PDF data return in Docusign response");
        } else alert("Invalid Docusign data returned");
      } else alert("Invalid Docusign PDF response receieved");
    });
  };

  const resendDocusign = async (envelope) => {
    const resp = await resendEnvelope(envelope);
    console.log("Resend Response", resp);
    toast.success("Resent Envelope Emails");
  };

  const getVoidReason = () => {
    const reason = window.prompt(
      "Voiding cannot be undone and ALL documents in the Docusign envelope will be voided. All recipients will receive an email that documents will be voided. Please specify a reason for the void:"
    );
    if (reason === "") return getVoidReason();
    else return reason;
  };

  // const getEnvelopeDetails = (envelope) => {
  //   const envelopeJSON = JSON.parse(envelope.jsonData);

  //   const signers =
  //     envelopeJSON.recipients.signers &&
  //     envelopeJSON.recipients.signers.length > 0 ? (
  //       <>
  //         <div>Signers</div>
  //         {envelopeJSON.recipients.signers.map((signer, index) => (
  //           <div
  //             key={`${signer.name}${index}`}
  //             className={
  //               signer.completedCount && signer.completedCount > 0
  //                 ? "ms-4 fw-normal"
  //                 : "ms-4 text-danger"
  //             }
  //           >
  //             {`${signer.name}: ${
  //               signer.completedCount && signer.completedCount > 0
  //                 ? `Signed ${new Date(signer.signedDateTime).toLocaleString()}`
  //                 : "Not Signed"
  //             }`}
  //           </div>
  //         ))}
  //       </>
  //     ) : null;

  //   const inPersonSigners =
  //     envelopeJSON.recipients.inPersonSigners &&
  //     envelopeJSON.recipients.inPersonSigners.length > 0 ? (
  //       <>
  //         <div>In Person Signers</div>
  //         {envelopeJSON.recipients.inPersonSigners.map((signer, index) => (
  //           <div
  //             key={`${signer.name}${index}`}
  //             className={
  //               signer.completedCount && signer.completedCount > 0
  //                 ? "ms-4 fw-normal"
  //                 : "ms-4 text-danger"
  //             }
  //           >
  //             {`${signer.signerName}(host: ${signer.hostName}): ${
  //               signer.completedCount && signer.completedCount > 0
  //                 ? `Signed ${new Date(signer.signedDateTime).toLocaleString()}`
  //                 : "Not Signed"
  //             }`}
  //           </div>
  //         ))}
  //       </>
  //     ) : null;

  //   const envelopeDocuments = envelope.Documents.filter(
  //     (doc) => doc.name !== "Summary"
  //   );

  //   const docs = envelopeDocuments.map((doc) => (
  //     <div
  //       key={doc.documentIdGuid}
  //       className={
  //         completed
  //           ? "rounded border border-dark p-2 m-2 bg-success text-dark"
  //           : "rounded border border-dark p-2 m-2 bg-warning text-dark"
  //       }
  //     >
  //       <div className="row">
  //         <div className="col-10 fw-bold">{doc.name}</div>
  //         {envelopeJSON.envelopeDocuments &&
  //         envelopeJSON.envelopeDocuments[0].PDFBytes ? (
  //           <i
  //             className="fas fa-file-pdf fa-2x text-white"
  //             onClick={() => {
  //               window.open(
  //                 URL.createObjectURL(
  //                   base64toBlob(envelopeJSON.envelopeDocuments[0].PDFBytes)
  //                 )
  //               );
  //             }}
  //           ></i>
  //         ) : (
  //           <></>
  //         )}
  //         {envelope === null ? (
  //           <i
  //             className="fas fa-file-pdf fa-2x text-white"
  //             onClick={() => {
  //               openDocusignPDF(envelope.envelopId);
  //             }}
  //           ></i>
  //         ) : (
  //           <></>
  //         )}
  //       </div>
  //     </div>
  //   ));

  //   const status = (
  //     <div className="row">
  //       <div className="col-6">{`Created: ${new Date(
  //         envelopeJSON.sentDateTime
  //       ).toLocaleString()}`}</div>
  //       <div className="col-6">
  //         {completed
  //           ? `Completed: ${new Date(
  //               envelopeJSON.completedDateTime
  //             ).toLocaleString()}`
  //           : `Status: ${envelopeJSON.status}`}
  //       </div>
  //     </div>
  //   );

  //   const completed = envelope.status === "completed";

  //   return (
  //     <div
  //       className="rounded mx-2 my-4 p-2 border-dark safy-border-2 bg-light fw-bold shadow"
  //       key={envelope.envelopeId}
  //     >
  //       <div className="row">
  //         <div className="col-5">{docs}</div>
  //         <div className="col-7">
  //           {status}
  //           {signers}
  //           {inPersonSigners}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const statusColor = (doc) => {
    if (doc.autocompleted === true) {
      return (
        <div className="bg-secondary text-white rounded px-2 fw-bold col-6 col-xs-6 col-sm-6 col-md-6 col-lg-12 col-xl-12">
          SAFY Auto Completed
        </div>
      );
    }
    switch (doc.status.toLowerCase()) {
      case "fully signed":
        return (
          <div className="bg-success rounded px-2 fw-bold col-6 col-xs-6 col-sm-6 col-md-6 col-lg-12 col-xl-12">
            Complete
          </div>
        );
      case "voided":
      case "declined":
        return (
          <div className="bg-danger rounded px-2 fw-bold col-6 col-xs-6 col-sm-6 col-md-6 col-lg-12 col-xl-12">
            {doc.status[0].toUpperCase() + doc.status.slice(1)}
          </div>
        );
      case "safyautocompleted":
        return (
          <div className="bg-secondary text-white rounded px-2 fw-bold col-6 col-xs-6 col-sm-6 col-md-6 col-lg-12 col-xl-12">
            SAFY Auto Completed
          </div>
        );
      default:
        return (
          <div className="bg-warning rounded px-2 fw-bold col-6 col-xs-6 col-sm-6 col-md-6 col-lg-12 col-xl-12">
            {doc.status[0].toUpperCase() + doc.status.slice(1)}
          </div>
        );
    }
  };

  const openDocusignPDF = async (id) => {
    //alert(id);
    getDocusignPDF(id).then((response) => {
      if (response) {
        const pdfTransaction = JSON.parse(response);
        console.log("pdfTransaction", pdfTransaction);
        if (
          pdfTransaction.Docusign &&
          pdfTransaction.Docusign.Documents &&
          pdfTransaction.Docusign.Documents[0] &&
          pdfTransaction.Docusign.Documents[0].jsonData
        ) {
          const pdfJSON = JSON.parse(
            pdfTransaction.Docusign.Documents[0].jsonData
          );
          if (
            pdfJSON &&
            pdfJSON.envelopeDocuments &&
            pdfJSON.envelopeDocuments[0].PDFBytes
          ) {
            const pdfBytes = pdfJSON.envelopeDocuments[0].PDFBytes;
            console.log(pdfBytes);
            window.open(URL.createObjectURL(base64toBlob(pdfBytes)));
          } else alert("No PDF data return in Docusign response");
        } else alert("Invalid Docusign data returned");
      } else alert("Invalid Docusign PDF response receieved");
    });
  };

  const addNote = async () => {
    const obj = {
      command: "insert",
      objectid: selectedNotes.id,
      objecttype: selectedNotes.type === "formsy" ? "formsy" : "docusign",
      employeeid: safyInfo.user.email,
      employeename: safyInfo.user.displayName,
      commentdate: new Date().toLocaleString(),
      comment: note,
      clientid: contactId,
      clientprogramid: clientProgramId,
    };

    saveFormComment(obj).then((response) => {
      if (response.SystemResponse[0].Code === "success") {
        setNote("");
        updateFormNotes();
        toast.success("Form Note Added Successfully");
      } else {
        toast.error("Error Adding Form Note");
      }
    });
  };

  const editNote = async () => {
    const note = formNotes.find((c) => c.CommentId === selectedNote);
    const obj = {
      command: "update",
      objectid: note.ObjectId,
      objecttype: note.ObjectType,
      employeeid: note.EmployeeId,
      employeename: note.EmployeeName,
      commentdate: note.CommentDateUTC,
      comment: noteText,
      clientid: note.ClientId,
      clientprogramid: note.ClientProgramId,
      commentid: note.CommentId,
    };

    saveFormComment(obj).then((response) => {
      if (response.SystemResponse[0].Code === "success") {
        setNote("");
        setSelectedNote(null);
        setNoteText("");
        updateFormNotes();
        toast.success("Form Note Edited Successfully");
      } else {
        toast.error("Error Editing Form Note");
      }
    });
  };

  return (
    <>
      <Modal
        show={selectedNotes !== null}
        animation={false}
        size="lg"
        onHide={() => {
          setSelectedNotes(null);
          setSelectedNote(null);
        }}
      >
        <Modal.Header>
          <Modal.Title>
            {selectedNotes !== null &&
              `${selectedNotes.name}-${selectedNotes.date}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNotes !== null && (
            <div className="border rounded mb-4 p-2">
              <div className="h5">Notes</div>
              {formNotes.filter(
                (comment) => comment.ObjectId === selectedNotes.id
              ).length > 0
                ? formNotes
                    .filter((comment) => comment.ObjectId === selectedNotes.id)
                    .map((comment) => (
                      <div key={`comment${comment.CommentId}`} className="m-4">
                        <div
                          className={
                            comment.EmployeeId === safyInfo.user.email
                              ? "row justify-content-end"
                              : "row"
                          }
                        >
                          <div className="col-6 fw-bold justify-content-end">
                            {comment.EmployeeId === safyInfo.user.email && (
                              <>
                                <i
                                  className="fas fa-trash-alt text-danger ms-2 float-end"
                                  onClick={() => {
                                    deactivateFormComment(
                                      comment.CommentId
                                    ).then((response) => {
                                      if (
                                        response.SystemResponse[0].Code ===
                                        "success"
                                      ) {
                                        updateFormNotes();
                                        setSelectedNote(null);
                                        toast.success(
                                          "Form Note Deleted Successfully"
                                        );
                                      } else {
                                        toast.error("Error Deleting Form Note");
                                      }
                                    });
                                  }}
                                ></i>
                                {selectedNote !== comment.CommentId ? (
                                  <i
                                    className="fas fa-pencil float-end text-primary"
                                    onClick={() => {
                                      toast.success(
                                        `Edit ${comment.CommentId}`
                                      );
                                      setSelectedNote(comment.CommentId);
                                      setNoteText(comment.Comment);
                                    }}
                                  ></i>
                                ) : null}
                              </>
                            )}

                            <div
                              className={
                                comment.EmployeeId === safyInfo.user.email
                                  ? "float-end"
                                  : ""
                              }
                            >
                              <div>{comment.EmployeeName}</div>
                              <div>
                                {new Date(
                                  comment.CommentDateUTC
                                ).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          {comment.CommentId === selectedNote ? (
                            <div className="border rounded bg-white p-2">
                              <Input
                                name="note"
                                label=""
                                value={noteText}
                                onChange={({ currentTarget: input }) =>
                                  setNoteText(input.value)
                                }
                              />
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  editNote();
                                }}
                              >
                                Save
                              </button>
                              <button
                                className="btn btn-sm btn-danger mx-2"
                                onClick={() => {
                                  setSelectedNote(null);
                                  setNoteText("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="border rounded bg-light p-2">
                              {comment.Comment}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                : "No Notes"}
            </div>
          )}
          <div className="row mt-4 clearfix">
            <div className="col-9">
              <Input
                name="notemessage"
                label=""
                value={note}
                onChange={({ currentTarget: input }) => setNote(input.value)}
              />
            </div>
            <div className="col-3">
              <button className="btn btn-sm btn-primary" onClick={addNote}>
                Add Note
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-danger"
            onClick={() => {
              setSelectedNotes(null);
              setSelectedNote(null);
            }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
      <DocusignAuditHistory
        auditEvents={auditHistory}
        isOpen={auditIsOpen}
        hideModal={() => setAuditIsOpen(!auditIsOpen)}
      ></DocusignAuditHistory>
      <div>
        <div className="row mb-4">
          <div className="col-11 h5">Forms for {clientName}</div>
          <div className="col-1">
            <i
              className="fas fa-sync text-primary pe-4"
              onClick={getHistory}
            ></i>
          </div>
        </div>

        {/* {historyView === "Envelopes" && Object.keys(envelopeHistory).length > 0
        ? map(envelopeHistory, (envelope, index) => (
            <div key={`${envelope.envelopeId}${index}`}>
              {getEnvelopeDocuments(envelope)}
            </div>
          ))
        : null} */}

        <div>
          <div className="row">
            <div className="col-lg-6 col-xl-6">
              <SearchBox
                divClassName="mb-4"
                label="Search history by document name"
                value={searchQuery}
                placeholder="Search Document Name"
                onChange={handleSearch}
                clearSearch={() => {
                  setSearchQuery("");
                  filterDocuments("");
                }}
                noBorder={true}
              />
            </div>
            <div className="col-lg-3 col-xl-3 d-flex">
              <Input
                name="beginDate"
                type="date"
                label="Begin Date"
                value={beginDate}
                onChange={handleBeginDate}
              />
              <i
                className="fas fa-times-circle text-danger pt-2"
                onClick={() => {
                  setBeginDate("");
                  filterDocuments();
                }}
              ></i>
            </div>
            <div className="col-lg-3 col-xl-3 d-flex">
              <Input
                name="endDate"
                type="date"
                label="End Date"
                value={endDate}
                onChange={handleEndDate}
              />
              <i
                className="fas fa-times-circle text-danger pt-2"
                onClick={() => {
                  setEndDate("");
                  filterDocuments();
                }}
              ></i>
            </div>
          </div>
          <div className="btn-group mb-4">
            <div
              className={
                historyView === "Summary"
                  ? "btn btn-success"
                  : "btn btn-secondary"
              }
              onClick={() => setHistoryView("Summary")}
            >
              Summary
            </div>
            <div
              className={
                historyView === "All" ? "btn btn-success" : "btn btn-secondary"
              }
              onClick={() => setHistoryView("All")}
            >
              All
            </div>
            <div
              className={
                historyView === "InProcess"
                  ? "btn btn-success"
                  : "btn btn-secondary"
              }
              onClick={() => setHistoryView("InProcess")}
            >
              In Process
            </div>
            <div
              className={
                historyView === "Completed"
                  ? "btn btn-success"
                  : "btn btn-secondary"
              }
              onClick={() => setHistoryView("Completed")}
            >
              Completed
            </div>
          </div>
          {!formHistoryLoaded ? (
            <div className="d-flex justify-content-center">
              <div style={{ width: "500px" }}>
                <div className="d-flex justify-content-center align-items-center">
                  <h1>LOADING Documents</h1>
                  <Lottie
                    animationData={documentsLoading.default}
                    loop={true}
                    autoplay={true}
                    rendererSettings={{
                      preserveAspectRatio: "xMidYMid slice",
                    }}
                    height={120}
                    width={120}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {historyView !== "Summary" &&
                filterDocuments().map((doc, index) => (
                  <div
                    key={`${doc.documentIdGuid}${index}`}
                    className={
                      doc.type !== "formsy" &&
                      doc.envelope.jsonData.includes("autoResponded")
                        ? `rounded p-2 m-2 border bg-warning text-dark`
                        : `rounded p-2 m-2 border bg-light text-dark`
                    }
                    title={doc.status}
                  >
                    <div className="row">
                      <div className="col-10 fw-bold">
                        <div className="row mb-0 pb-0">
                          <div className="col-10">{doc.name}</div>
                          <div className="col-2 pt-1">
                            {/* docusign with embeded pdf (old) */}
                            {doc.PDFBytes ? (
                              <i
                                className="fas fa-file-pdf fa-lg text-safy-info float-end"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  window.open(
                                    URL.createObjectURL(
                                      base64toBlob(doc.PDFBytes)
                                    )
                                  );
                                }}
                              ></i>
                            ) : null}
                            {/* docusign with separate pdf (new way) */}
                            {(doc.status === "Fully Signed" ||
                              doc.status === "safyautocompleted" ||
                              doc.status === "voided") &&
                            !doc.PDFBytes &&
                            !doc.SubmissionId ? (
                              <>
                                <i
                                  className="fas fa-file-pdf text-primary float-end"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    openDocusignPDF(doc.envelopeId);
                                  }}
                                ></i>
                                <i
                                  title="Docusign Certificate"
                                  className="fas fa-history float-end me-2"
                                  onClick={() =>
                                    openDocusignCertificate(doc.envelopeId)
                                  }
                                ></i>
                              </>
                            ) : null}
                            {doc.envelopeId &&
                              doc.status.toLowerCase() !== "voided" &&
                              doc.status.toLowerCase() !== "fully signed" &&
                              doc.status.toLowerCase() !==
                                "safyautocompleted" &&
                              doc.status.toLowerCase() !== "draft" &&
                              doc.status.toLowerCase() !== "expired" &&
                              doc.status.toLowerCase() !== "declined" && (
                                <>
                                  <i
                                    title="Void"
                                    className="far fa-times-circle text-danger float-end ps-2"
                                    onClick={() => {
                                      voidEnvelope(doc.envelopeId);
                                      //DO Something else
                                    }}
                                  ></i>
                                  <i
                                    title="Resend"
                                    className="far fa-envelope float-end ps-2"
                                    onClick={() => {
                                      resendDocusign(
                                        JSON.parse(doc.envelope.jsonData)
                                      );
                                      //DO Something else
                                    }}
                                  ></i>
                                  <i
                                    title="Open in Docusign"
                                    className="far fa-eye float-end ps-2"
                                    onClick={() => {
                                      openInDocusign(doc.envelopeId);
                                      //DO Something else
                                    }}
                                  ></i>
                                  <i
                                    title="Audit History"
                                    className="fas fa-history float-end"
                                    onClick={() =>
                                      openDocusignHistory(doc.envelopeId)
                                    }
                                  ></i>
                                </>
                              )}
                            {/* formsy document */}
                            {doc.type === "formsy" && doc.name ? (
                              <Link
                                title={`View ${doc.name}`}
                                className={
                                  doc.status === "Fully Signed"
                                    ? "fas fa-file float-end"
                                    : "fas fa-pen float-end"
                                }
                                to={`/${doc.formPath}${
                                  doc.formPath !== "form" &&
                                  doc.formVersionNumber > 1
                                    ? `v${doc.formVersionNumber}`
                                    : ""
                                }/${doc.FormVersionId}/${doc.ContactId}/${
                                  doc.SubmissionId
                                }`}
                                state={{
                                  tab: "forms",
                                  previousPath: location.pathname,
                                  selectedProgram: clientProgramId,
                                }}
                                target="_blank"
                              />
                            ) : null}
                            <i
                              className={`fas fa-comment-alt float-end pe-2 ${
                                formNotes.filter(
                                  (c) =>
                                    c.ObjectId === doc.envelopeId ||
                                    c.ObjectId === doc.SubmissionId
                                ).length > 0
                                  ? "text-warning"
                                  : ""
                              }`}
                              title={formNotes
                                .filter(
                                  (n) =>
                                    n.ObjectId == doc.envelopeId ||
                                    n.ObjectId == doc.SubmissionId
                                )
                                .map(
                                  (n) =>
                                    `${new Date(
                                      n.CreatedOnUTC
                                    ).toLocaleDateString()}: ${n.Comment}\n`
                                )}
                              onClick={() => {
                                setSelectedNotes({
                                  id: doc.envelopeId || doc.SubmissionId,
                                  type: doc.type,
                                  name: doc.name,
                                  date: new Date(doc.date).toLocaleString(),
                                });
                                setSelectedNote(null);
                              }}
                            ></i>
                          </div>
                        </div>
                        {new Date(doc.date).toLocaleString()}
                      </div>
                      <div className="col-lg-2 col-xl-2">
                        {statusColor(doc)}
                        {doc.envelope &&
                          doc.envelope.jsonData &&
                          JSON.parse(doc.envelope.jsonData).voidedReason}
                      </div>
                    </div>
                    {doc.envelopeId ? (
                      <ExpandDiv
                        title={
                          doc.envelope.jsonData.includes("autoResponded") && (
                            <i className="fas fa-exclamation text-danger mx-4" />
                          )
                        }
                      >
                        {getEnvelopeDocuments(
                          envelopeHistory[doc.envelopeId],
                          doc.autocompleted
                        )}
                      </ExpandDiv>
                    ) : null}
                  </div>
                ))}
              {historyView === "Summary" &&
                Object.values(summaryDocuments())
                  .sort((a, b) => {
                    if (a[0].name > b[0].name) return 1;
                    if (a[0].name < b[0].name) return -1;
                    return 0;
                  })
                  .filter(
                    (transaction) => transaction.status !== "safyautocompleted"
                  )
                  .map((key, i) => (
                    <ExpandDiv
                      key={`summary${i}`}
                      title={
                        <div
                          className={
                            key[0].type !== "formsy" &&
                            key[0].envelope.jsonData.includes("autoResponded")
                              ? "d-inline-block rounded bg-warning p-2"
                              : "d-inline-block p-2"
                          }
                        >
                          <small className="d-inline rounded safy-border-2  border-white px-2 pb-1 mx-2>">{`${key.length}`}</small>
                          <div className="d-inline"> {`${key[0].name}`}</div>
                          {key[0].type !== "formsy" &&
                            key[0].envelope.jsonData.includes(
                              "autoResponded"
                            ) && (
                              <i className="fas fa-exclamation text-danger mx-4" />
                            )}
                        </div>
                      }
                      className="mb-1"
                    >
                      {key.map((transaction, x) => (
                        <div
                          key={`summary${transaction.documentIdGuid}${x}`}
                          className={
                            transaction.type !== "formsy" &&
                            transaction.envelope.jsonData.includes(
                              "autoResponded"
                            )
                              ? `rounded p-2 m-2 border bg-warning text-dark`
                              : `rounded p-2 m-2 border bg-light text-dark`
                          }
                          title={transaction.status}
                        >
                          <div className="row">
                            <div className="col-10 fw-bold">
                              <div className="row mb-0 pb-0">
                                <div className="col-10">{transaction.name}</div>
                                <div className="col-2 pt-1">
                                  {/* docusign with embeded pdf (old) */}
                                  {transaction.PDFBytes ? (
                                    <i
                                      className="fas fa-file-pdf fa-lg text-safy-info float-end"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        window.open(
                                          URL.createObjectURL(
                                            base64toBlob(transaction.PDFBytes)
                                          )
                                        );
                                      }}
                                    ></i>
                                  ) : null}
                                  {/* docusign with separate pdf (new way) */}
                                  {(transaction.status === "Fully Signed" ||
                                    transaction.status ===
                                      "safyautocompleted" ||
                                    transaction.status === "voided") &&
                                  !transaction.PDFBytes &&
                                  !transaction.SubmissionId ? (
                                    <>
                                      <i
                                        className="fas fa-file-pdf fa-lg text-primary float-end pt-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                          openDocusignPDF(
                                            transaction.envelopeId
                                          );
                                        }}
                                      ></i>
                                      <i
                                        title="Docusign Certificate"
                                        className="fas fa-history float-end me-2"
                                        onClick={() =>
                                          openDocusignCertificate(
                                            transaction.envelopeId
                                          )
                                        }
                                      ></i>
                                    </>
                                  ) : null}
                                  {transaction.envelopeId &&
                                    transaction.status.toLowerCase() !==
                                      "safyautocompleted" &&
                                    transaction.status.toLowerCase() !==
                                      "voided" &&
                                    transaction.status.toLowerCase() !==
                                      "fully signed" &&
                                    transaction.status.toLowerCase() !==
                                      "draft" &&
                                    transaction.status.toLowerCase() !==
                                      "expired" &&
                                    transaction.status.toLowerCase() !==
                                      "declined" && (
                                      <>
                                        <i
                                          title="Void"
                                          className="far fa-times-circle text-danger float-end ps-2"
                                          onClick={() => {
                                            voidEnvelope(
                                              transaction.envelopeId
                                            );
                                            //DO Something else
                                          }}
                                        ></i>
                                        <i
                                          title="Resend"
                                          className="far fa-envelope float-end ps-2"
                                          onClick={() => {
                                            resendDocusign(
                                              JSON.parse(
                                                transaction.envelope.jsonData
                                              )
                                            );
                                            //DO Something else
                                          }}
                                        ></i>
                                        <i
                                          title="Open in Docusign"
                                          className="far fa-eye float-end ps-2"
                                          onClick={() => {
                                            openInDocusign(
                                              transaction.envelopeId
                                            );
                                            //DO Something else
                                          }}
                                        ></i>
                                      </>
                                    )}
                                  {/* formsy document */}
                                  {transaction.type === "formsy" &&
                                  transaction.name ? (
                                    <Link
                                      title={`View ${transaction.name}`}
                                      className={
                                        transaction.status === "Fully Signed"
                                          ? "fas fa-file float-end"
                                          : "fas fa-pen float-end"
                                      }
                                      to={`/${transaction.formPath}${
                                        transaction.formPath !== "form" &&
                                        transaction.formVersionNumber > 1
                                          ? `v${transaction.formVersionNumber}`
                                          : ""
                                      }/${transaction.FormVersionId}/${
                                        transaction.ContactId
                                      }/${transaction.SubmissionId}`}
                                      state={{
                                        tab: "forms",
                                        previousPath: location.pathname,
                                        selectedProgram: clientProgramId,
                                      }}
                                      target="_blank"
                                    />
                                  ) : null}
                                  <i
                                    className={`fas fa-comment-alt float-end pe-2 ${
                                      formNotes.filter(
                                        (c) =>
                                          c.ObjectId ===
                                            transaction.envelopeId ||
                                          c.ObjectId ===
                                            transaction.SubmissionId
                                      ).length > 0
                                        ? "text-warning"
                                        : ""
                                    }`}
                                    title={formNotes
                                      .filter(
                                        (n) =>
                                          n.ObjectId ==
                                            transaction.envelopeId ||
                                          n.ObjectId == transaction.SubmissionId
                                      )
                                      .map(
                                        (n) =>
                                          `${new Date(
                                            n.CreatedOnUTC
                                          ).toLocaleDateString()}: ${
                                            n.Comment
                                          }\n`
                                      )}
                                    onClick={() => {
                                      setSelectedNotes({
                                        id:
                                          transaction.envelopeId ||
                                          transaction.SubmissionId,
                                        type: transaction.type,
                                        name: transaction.name,
                                        date: new Date(
                                          transaction.date
                                        ).toLocaleString(),
                                      });
                                      setSelectedNote(null);
                                    }}
                                  ></i>
                                </div>
                              </div>
                              {transaction.extraFields
                                ? transaction.extraFields
                                    .filter(
                                      (field) =>
                                        field.value !== undefined &&
                                        field.value !== ""
                                    )
                                    .map((field) => (
                                      <div key={field.name}>
                                        {field.displayText}: {field.value}
                                      </div>
                                    ))
                                : null}
                              {new Date(transaction.date).toLocaleString()}
                            </div>
                            <div className="col-lg-2 col-xl-2">
                              {statusColor(transaction)}
                              {transaction.envelope &&
                                transaction.envelope.jsonData &&
                                JSON.parse(transaction.envelope.jsonData)
                                  .voidedReason}
                            </div>
                          </div>
                          {transaction.envelopeId ? (
                            <ExpandDiv>
                              {getEnvelopeDocuments(
                                envelopeHistory[transaction.envelopeId],
                                transaction.autocompleted
                              )}
                            </ExpandDiv>
                          ) : null}
                        </div>
                      ))}
                    </ExpandDiv>
                  ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default FormHistory;
